import { useEffect, useRef, useState } from 'react';
import { useUserContext } from 'src/context/user';
import {
  hasSeenFollowLocals,
  isDayZeroUser,
  markFollowLocalsSeen,
  recordDayZeroLearnSession,
} from './dayZeroSessions';

export interface FollowLocalsGate {
  pending: boolean;
  eligible: boolean;
}

/**
 * Offers the follow-locals screen once a day-zero learner has finished at least
 * three Learn sessions, on the first of those that showed a video. An empty
 * session does not mark the offer seen, so a later one can still offer it.
 * Dismissing marks it seen. The caller decides whether this learner is in the
 * Spanish Path cohort. The count is written after commit so a render does not
 * touch localStorage.
 */
export const useFollowLocalsOffer = (
  sessionCompleted: boolean,
  gate: FollowLocalsGate,
  hasSessionVideos: boolean,
) => {
  const { user, loading: userLoading } = useUserContext();
  const recorded = useRef(false);
  const [phase, setPhase] = useState<'undecided' | 'show' | 'skip'>('undecided');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!sessionCompleted || recorded.current || gate.pending || userLoading) return;
    recorded.current = true;

    if (!gate.eligible || !user || !isDayZeroUser(user.dateJoined)) {
      setPhase('skip');
      return;
    }

    const completedCount = recordDayZeroLearnSession(user.id);
    setPhase(
      completedCount >= 3 && !hasSeenFollowLocals(user.id) && hasSessionVideos ? 'show' : 'skip',
    );
  }, [sessionCompleted, gate.pending, gate.eligible, hasSessionVideos, user, userLoading]);

  const dismiss = () => {
    if (user) markFollowLocalsSeen(user.id);
    setDismissed(true);
  };

  // A gate that has already ruled the learner out is not pending on this render.
  // Eligible sessions stay pending until the effect chooses show or skip.
  const gateResolvedIneligible = !gate.pending && !gate.eligible;

  return {
    show: phase === 'show' && !dismissed,
    pending: sessionCompleted && phase === 'undecided' && !gateResolvedIneligible,
    dismiss,
  };
};
